<template>
  <v-container>
    <v-tabs v-model="activeTab" class="mb-6" color="primary">
      <v-tab to="/admin/forms">Formulaires</v-tab>
      <v-tab to="/admin/alerts">Alertes</v-tab>
      <v-tab to="/admin/communiques">Communiqués</v-tab>
      <v-spacer></v-spacer>
      <v-btn text @click="logout" class="mt-2 mr-2">
        Déconnexion <v-icon right small>mdi-logout</v-icon>
      </v-btn>
    </v-tabs>

    <v-card>
      <v-card-title>
        Gestion des Communiqués de Presse
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Rechercher"
          single-line
          hide-details
          class="mr-4"
        ></v-text-field>
        <v-btn color="primary" @click="openDialog()">
          <v-icon left>mdi-plus</v-icon>
          Nouveau Communiqué
        </v-btn>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="communiques"
        :loading="loading"
        :search="search"
        loading-text="Chargement des communiqués..."
      >
        <template v-slot:item.active="{ item }">
          <v-chip :color="item.active ? 'success' : 'grey'" small>
            {{ item.active ? 'Actif' : 'Inactif' }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mr-2" color="primary" @click="openDialog(item)">
            mdi-pencil
          </v-icon>
          <v-icon small color="error" @click="confirmDelete(item)">
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <!-- Communique Dialog -->
    <v-dialog v-model="dialog" max-width="1100px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" md="8">
                <v-text-field v-model="editedItem.title" label="Titre du Communiqué" required></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-checkbox v-model="editedItem.active" label="Actif / Visible"></v-checkbox>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field v-model="editedItem.src" label="URL de l'image (logo/visuel)"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="editedItem.cta" label="Texte du bouton (CTA)"></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-textarea v-model="editedItem.desc" label="Description courte (pour l'aperçu)" rows="2"></v-textarea>
              </v-col>

              <v-col cols="12">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="subtitle-2">Contenu détaillé (Rich Text)</div>
                  <v-btn small text color="primary" @click="showHtml = !showHtml">
                    <v-icon left small>{{ showHtml ? 'mdi-eye' : 'mdi-xml' }}</v-icon>
                    {{ showHtml ? 'Aperçu HTML' : 'Afficher HTML' }}
                  </v-btn>
                </div>
                
                <vue-editor 
                  v-if="!showHtml"
                  v-model="editedItem.content.content" 
                />
                <v-textarea
                  v-else
                  v-model="editedItem.content.content"
                  label="Aperçu du code HTML"
                  readonly
                  outlined
                  rows="12"
                  background-color="grey lighten-4"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeDialog">Annuler</v-btn>
          <v-btn color="primary" :loading="saveLoading" @click="save">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirmation</v-card-title>
        <v-card-text>Supprimer ce communiqué ? Cette action est irréversible.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="deleteDialog = false">Non</v-btn>
          <v-btn color="error" @click="doDelete">Oui, Supprimer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';
import { VueEditor } from 'vue2-editor';

export default {
  name: 'AdminCommuniques',
  components: {
    VueEditor
  },
  data: () => ({
    activeTab: 2,
    loading: false,
    search: '',
    saveLoading: false,
    dialog: false,
    deleteDialog: false,
    headers: [
      { text: 'Titre', value: 'title' },
      { text: 'CTA', value: 'cta' },
      { text: 'Statut', value: 'active' },
      { text: 'Créé le', value: 'createdAt' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'end' },
    ],
    communiques: [],
    editedIndex: -1,
    editedItem: {
      title: '',
      content: {
        type: 'html',
        content: ''
      },
      src: '/img/logo-fondIndePresse.133b7800.jpg',
      desc: '',
      cta: 'Lire le communiqué',
      active: true
    },
    defaultItem: {
      title: '',
      content: {
        type: 'html',
        content: ''
      },
      src: '/img/logo-fondIndePresse.133b7800.jpg',
      desc: '',
      cta: 'Lire le communiqué',
      active: true
    },
    itemToDelete: null,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
    showHtml: false
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'Nouveau Communiqué' : 'Modifier le Communiqué';
    },
  },

  mounted() {
    this.fetchCommuniques();
  },

  methods: {
    getHeaders() {
      const token = localStorage.getItem('adminToken');
      return {
        headers: { 'x-access-token': token }
      };
    },

    async fetchCommuniques() {
      this.loading = true;
      try {
        const response = await axios.get(`${process.env.VUE_APP_URL}admin/api/communiques`, this.getHeaders());
        this.communiques = response.data.communiques;
      } catch (err) {
        const message = (err.response && err.response.data && err.response.data.message) || 'Erreur lors de la récupération';
        this.showSnackbar(message, 'error');
        if (err.response && err.response.status === 401) this.logout();
      } finally {
        this.loading = false;
      }
    },

    openDialog(item) {
      if (item) {
        this.editedIndex = this.communiques.indexOf(item);
        this.editedItem = JSON.parse(JSON.stringify(item));
        // Ensure content structure exists
        if (!this.editedItem.content) this.editedItem.content = { type: 'html', content: '' };
      } else {
        this.editedIndex = -1;
        this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
      }
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
    },

    async save() {
      if (!this.editedItem.title || !this.editedItem.content.content) {
        this.showSnackbar('Le titre et le contenu sont obligatoires', 'warning');
        return;
      }

      this.saveLoading = true;
      try {
        if (this.editedIndex > -1) {
          const id = this.editedItem._id;
          await axios.put(`${process.env.VUE_APP_URL}admin/api/communiques/${id}`, this.editedItem, this.getHeaders());
          this.showSnackbar('Communiqué mis à jour');
        } else {
          await axios.post(`${process.env.VUE_APP_URL}admin/api/communiques`, this.editedItem, this.getHeaders());
          this.showSnackbar('Communiqué créé');
        }
        this.fetchCommuniques();
        this.closeDialog();
      } catch (err) {
        const message = (err.response && err.response.data && err.response.data.message) || 'Erreur lors de l\'enregistrement';
        this.showSnackbar(message, 'error');
      } finally {
        this.saveLoading = false;
      }
    },

    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    async doDelete() {
      try {
        await axios.delete(`${process.env.VUE_APP_URL}admin/api/communiques/${this.itemToDelete._id}`, this.getHeaders());
        this.showSnackbar('Communiqué supprimé');
        this.fetchCommuniques();
      } catch (err) {
        const message = (err.response && err.response.data && err.response.data.message) || 'Erreur lors de la suppression';
        this.showSnackbar(message, 'error');
      } finally {
        this.deleteDialog = false;
        this.itemToDelete = null;
      }
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    logout() {
      localStorage.removeItem('adminToken');
      this.$router.push({ name: 'AdminLogin' });
    }
  },
};
</script>
